import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface TimedProgressProps {
  /** Estimated duration in milliseconds */
  estimatedDurationMs: number;
  /** When the job started (ISO string or Date) */
  startTime: string | Date;
  /** Actual progress from server (0-100), used when job completes */
  serverProgress?: number;
  /** Whether the job is completed */
  isCompleted?: boolean;
  /** Whether the job failed */
  isFailed?: boolean;
  /** Error message if failed */
  errorMessage?: string;
  /** Custom class for the container */
  className?: string;
}

export function TimedProgress({
  estimatedDurationMs,
  startTime,
  serverProgress,
  isCompleted = false,
  isFailed = false,
  errorMessage,
  className = '',
}: TimedProgressProps) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [isOvertime, setIsOvertime] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (isCompleted) {
      setProgress(100);
      return;
    }

    if (isFailed) {
      return;
    }

    const calculateAndUpdate = () => {
      const start = new Date(startTime).getTime();
      const now = Date.now();
      const elapsed = now - start;
      setElapsedMs(elapsed);

      // Calculate time-based progress
      const rawProgress = (elapsed / estimatedDurationMs) * 100;

      // Cap at 95% if we're running over time
      if (rawProgress >= 95) {
        setIsOvertime(true);
        setProgress(95);
      } else {
        setProgress(Math.min(rawProgress, 95));
      }
    };

    // Initial calculation
    calculateAndUpdate();

    // Update progress every 500ms
    const interval = setInterval(calculateAndUpdate, 500);

    return () => clearInterval(interval);
  }, [startTime, estimatedDurationMs, isCompleted, isFailed]);

  // Use server progress if available and higher
  const displayProgress =
    serverProgress !== undefined && serverProgress > progress ? serverProgress : progress;

  // Calculate remaining time
  const remainingMs = Math.max(0, estimatedDurationMs - elapsedMs);
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const remainingMinutes = Math.floor(remainingSeconds / 60);
  const remainingSecondsRemainder = remainingSeconds % 60;

  const formatTime = (mins: number, secs: number) => {
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Progress bar */}
      <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${
            isFailed
              ? 'bg-red-500'
              : isCompleted
                ? 'bg-green-500'
                : isOvertime
                  ? 'bg-amber-500'
                  : 'bg-primary-500'
          }`}
          initial={{ width: 0 }}
          animate={{ width: `${displayProgress}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
        {/* Animated shimmer effect when processing */}
        {!isCompleted && !isFailed && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          />
        )}
      </div>

      {/* Status text */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {isFailed ? (
            <span className="text-red-600 dark:text-red-400">
              {errorMessage || t('backgroundJobs.failed')}
            </span>
          ) : isCompleted ? (
            <span className="text-green-600 dark:text-green-400">
              {t('backgroundJobs.completed')}
            </span>
          ) : isOvertime ? (
            <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('backgroundJobs.takingLonger')}
            </span>
          ) : (
            <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('backgroundJobs.processing')}
            </span>
          )}
        </div>

        <div className="text-gray-500 dark:text-gray-400">
          {isFailed || isCompleted ? (
            <span>{Math.round(displayProgress)}%</span>
          ) : isOvertime ? (
            <span>{Math.round(displayProgress)}%</span>
          ) : (
            <span>
              {Math.round(displayProgress)}% &middot;{' '}
              {t('backgroundJobs.timeRemaining', {
                time: formatTime(remainingMinutes, remainingSecondsRemainder),
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
